import { extendObservable, computed, action } from 'mobx'

import { expertApi, complianceBreachApi, massProcessingApi } from '@atheneum/api'

import ExpertModel from '@models/Expert.model'
import { highligthReplace } from '@atheneum/api/dist/lib'
import moment from 'moment'

class ExpertStore {
  constructor(staticData, growlStore, expertSearchStore) {
    extendObservable(this, {
      id: null,
      staticData,
      expert: null,
      expertCopy: null,
      loading: false,
      error: null,
      growlStore: growlStore,
      timezone: computed(this.getTimezone),
      industries: computed(this.getIndustries),
      subIndustries: computed(this.getSubIndustries),
      hourlyRate: computed(this.getHourlyRate),
      expertStatusId: null,
      lastActive: null,
      attachments: [],
      interviewCount: null,
      expertSearchStore,
      sherlock: null,
      elasticHighlight: {}
    })
  }

  statusChangeError = err => {
    this.error = err
    this.growlStore.showError(err)
  }

  changeExpertStatus = options => {
    return expertApi
      .toggleStatus(this.expert.id, options)
      .then(({ expertStatusId }) => {
        this.expertStatusId = expertStatusId
        this.growlStore.showSuccess(`Expert's status successfully changed.`)
      })
      .catch(this.statusChangeError)
  }

  setComplianceSigned = options => {
    return expertApi
      .setComplianceSigned(this.expert.id, options)
      .then(() => {
        this.growlStore.showSuccess(`Compliance successfully signed.`)
      })
      .catch(this.statusChangeError)
  }

  getExpertNameList(expertIds) {
    this.loading = true
    return expertApi.getExpertNames(expertIds).then(result => {
      this.loading = false
      return result
    })
  }

  get(id) {
    if (!id) return null
    this.loading = true

    if (!Number(id)) {
      return expertApi
        .getPDLExpert(id)
        .then(async expert => {
          try {
            const checked = await expertApi.checkExpert({
              emails: expert.emails
                ? expert.emails.map(e => e.email)
                : undefined,
              fullName: expert.fullName,
              linkedinVanityName: expert.linkedinVanityName,
              link: expert.linkedinVanityName
                ? `https://www.linkedin.com/in/${expert.linkedinVanityName}`
                : undefined
            })
            if (checked.id) {
              this.history.replace(
                `${this.history.location.pathname.includes('expert-search')
                  ? '/expert-search'
                  : ''
                }/expert/${checked.id}`
              )
            }
          } catch (error) {
            console.error(error.message)
          }

          this.expert = expert
          this.expertCopy = this.getExpertCopy(expert)
          this.expertStatusId = this.expert.expert.expertStatusId
          this.expert.experiences = expert.experiences.map(experience => {
            return {
              ...experience,
              company: { name: experience.company },
              position: { name: experience.position }
            }
          })
          this.expert.languages = expert.languages
          this.expert.cv.experiences = this.expert.experiences
          this.loading = false
        })
        .catch(err => {
          this.error = err
          this.loading = false
        })
    } else {
      this.id = Number(id)

      return expertApi
        .getOne(id)
        .then(expert => {
          this.expert = expert
          this.expertCopy = this.getExpertCopy(expert)
          this.expertStatusId = this.expert.expert.expertStatusId
          this.loading = false
        })
        .catch(err => {
          this.error = err
          this.loading = false
        })
    }
  }

  addHistory(history) {
    this.history = history
  }

  addSherlockRule(rule) {
    this.loading = true
    return complianceBreachApi
      .addRule(rule)
      .then(() => {
        this.loading = false
      })
      .catch(error => {
        throw new Error(error.message)
      })
  }

  loadData(id) {
    if (!id) return null

    this.loading = true
    this.expert = null
    this.expertCopy = null

    if (!Number(id) && id.startsWith("list-")) {
      // This is a list item
      // Example id: list-${expert.listId}_expert-${expert.id}
      this.id = id

      const [listString, listItemString] = id.split("_")
      const listId = listString.replace("list-", "")
      const listItemId = listItemString.replace("item-", "")

      massProcessingApi.getItem(listItemId).then(async item => {
        const expert = JSON.parse(JSON.stringify(item.data))

        // Special email mapping for AL
        expert.email = ""

        if (!expert.address) {
          expert.address = {}
        }

        if (!expert.address.emails) {
          expert.address.emails = []
        }

        if (Array.isArray(item.formatted?.emails)) {
          const allEmails = []
          for (let email of item.formatted?.emails) {
            if (typeof email === 'string' || email instanceof String) {
              allEmails.push(email)
            } else if (typeof email === 'object' && email.email) {
              allEmails.push(email.email)
            } else {
              // None of the above
            }
          }

          expert.address.emails = allEmails.map(e => ({ email: e.trim() }))
        } else if (typeof item.formatted?.emails === 'string' || item.formatted?.emails instanceof String) {

          const splitEmails = item.formatted.emails.split(",").map(e => e.trim()).filter(e => e)
          expert.address.emails = splitEmails.map(e => ({ email: e }))
        }

        if (expert.address.emails.length > 0) {
          expert.email = expert.address.emails[0].email
          expert.address.emails = expert.address.emails.filter(e => expert.email !== e.email)
        }

        // ADD PHONE MAPPING
        if (!expert.address.phones) {
          expert.address.phones = []
        }

        if (Array.isArray(expert.formatted?.phones)) {
          const allPhones = []
          for (let phone of expert.formatted?.phones) {
            if (typeof phone === 'string' || phone instanceof String) {
              allPhones.push(phone)
            } else if (typeof phone === 'object' && phone.phoneNum) {
              //allPhones.push(phone.phoneNum) // Not used currently
            } else {
              // None of the above
            }
          }

          expert.address.phones = allPhones.map(e => ({ phoneNum: e.trim() }))
        } else if (typeof expert.formatted?.phones === 'string' || expert.formatted?.phones instanceof String) {

          const splitPhones = expert.formatted.phones.split(",").map(e => e.trim()).filter(e => e)
          expert.address.phones = splitPhones.map(e => ({ phoneNum: e }))
        }

        expert.formatted = undefined

        try {
          const checked = await expertApi.checkExpert({
            emails: Array.isArray(expert.address?.emails)
              ? expert.address.emails.map(e => e.email)
              : undefined,
            fullName: expert.fullName,
            linkedinVanityName: expert.linkedinVanityName,
            link: expert.linkedinVanityName
              ? `https://www.linkedin.com/in/${expert.linkedinVanityName}`
              : undefined
          })
          if (checked.id) {
            this.history.replace(
              `${this.history.location.pathname.includes('expert-search')
                ? '/expert-search'
                : ''
              }/expert/${checked.id}`
            )
          }
        } catch (error) {
          console.error(error.message)
        }

        this.expert = expert
        this.expertCopy = this.getExpertCopy(this.expert)
        this.elasticHighlight = this.expertSearchStore.getExpertHighlights(
          expert.id
        )

        if (Array.isArray(expert.experiences) && expert.experiences.length > 0) {
          this.expert.experiences = expert.experiences
        } else if (Array.isArray(expert.cv?.experiences) && expert.cv.experiences.length > 0) {
          this.expert.experiences = expert.experiences
        } else {
          this.expert.experiences = []
        }

        // For proper rendering of experiences, object instead of string format is required
        this.expert.experiences.forEach(experience => {
          if (experience.position) {
            if (typeof experience.position === 'string') {
              experience.position = {
                id: experience.position,
                name: experience.position
              }
            } else {
              if (!experience.position.id) {
                experience.position.id = experience.position.name
              }
            }
          }

          if (experience.company) {
            if (typeof experience.company === 'string') {
              experience.company = {
                id: experience.company,
                name: experience.company
              }
            } else {
              if (!experience.company.id) {
                experience.company.id = experience.company.name
              }
            }
          }
        })

        this.expert.fullName = `${expert.firstName} ${expert.lastName}`

        this.expert.languages = expert.languages

        if (!this.expert.cv) {
          this.expert.cv = {}
        }

        this.expert.cv.experiences = this.expert.experiences
        this.sherlock = expert.sherlock
        this.expertStatusId = this.expert.expert?.expertStatusId
        this.lastActive = { lastActive: null }
        this.interviewCount = 0
        this.loading = false
      })
        .catch(err => {
          console.error(err.message)
          this.error = err
          this.loading = false
        })

    } else if (!Number(id)) {
      // This is a pdl
      // Example id: dvJsX1C0A6Q9d9Yx0bl-Ow_0000
      this.id = id

      expertApi
        .getPDLExpert(id)
        .then(async expert => {
          try {
            const checked = await expertApi.checkExpert({
              emails: Array.isArray(expert.address?.emails)
                ? expert.address.emails.map(e => e.email)
                : undefined,
              fullName: expert.fullName,
              linkedinVanityName: expert.linkedinVanityName,
              link: expert.linkedinVanityName
                ? `https://www.linkedin.com/in/${expert.linkedinVanityName}`
                : undefined
            })
            if (checked.id) {
              this.history.replace(
                `${this.history.location.pathname.includes('expert-search')
                  ? '/expert-search'
                  : ''
                }/expert/${checked.id}`
              )
            }
          } catch (error) {
            console.error(error.message)
          }

          this.expert = expert
          this.expertCopy = this.getExpertCopy(expert)
          this.elasticHighlight = this.expertSearchStore.getExpertHighlights(
            expert.id
          )

          this.expert.experiences = expert.experiences.map(experience => {
            return {
              ...experience,
              company: { name: experience.company },
              position: { name: experience.position }
            }
          })

          this.expert.languages = expert.languages
          this.expert.cv.experiences = this.expert.experiences
          this.sherlock = expert.sherlock
          this.expertStatusId = this.expert.expert.expertStatusId
          this.lastActive = { lastActive: null }
          this.interviewCount = 0
          this.loading = false
        })
        .catch(err => {
          this.error = err
          this.loading = false
        })
    } else {
      // This is a created expert
      // Example id: 620783
      this.id = Number(id)

      Promise.all([
        expertApi.getOne(id),
        expertApi.getAttachments(id),
        expertApi.getLastActive(id),
        expertApi.getInterviewCount(id),
        expertApi.checkSherlockOpinion(id).catch(() => ({
          allowed: false,
          display_reason: 'Unable to contact sherlock'
        }))
      ])
        .then(([expert, attachments, lastActive, interviewCount, sherlock]) => {
          this.expert = expert
          this.expertCopy = this.getExpertCopy(expert)
          this.elasticHighlight = this.expertSearchStore.getExpertHighlights(
            expert.id
          )

          this.expertStatusId = this.expert.expert.expertStatusId
          this.attachments.replace(attachments)
          this.lastActive = lastActive
          this.interviewCount = interviewCount
          this.sherlock = sherlock
          this.loading = false
        })
        .catch(err => {
          this.error = err
          this.loading = false
        })
    }
  }

  getExpertCopy(expert) {
    const hiReg = this.expertSearchStore.getHighlightsRegexp(expert.id)
    if (!hiReg) return expert

    try {
      return this.formatExpertDataWithHighlights(expert, hiReg)
    } catch (err) {
      return expert
    }
  }

  formatExpertDataWithHighlights(expert, hiReg) {
    const expertCopy = {
      ...expert,
      highlight: this.expertSearchStore.getExpertHighlight(expert.id)
    }

    expertCopy.expert.expertIndustryExperience = highligthReplace(
      expert.expert.expertIndustryExperience,
      hiReg
    )

    expertCopy.expert.industryExpertise = highligthReplace(
      expertCopy.expert.industryExpertise,
      hiReg
    )

    expertCopy.experiences = expert.cv.experiences.map(exp => {
      if (exp.company) {
        if (exp.company.name) {
          exp.company.name = highligthReplace(exp.company.name, hiReg)
        } else {
          exp.company = highligthReplace(exp.company, hiReg)
        }
      }
      if (exp.position) {
        if (exp.position.name) {
          exp.position.name = highligthReplace(exp.position.name, hiReg)
        } else {
          exp.position = highligthReplace(exp.position, hiReg)
        }
      }
      exp.description = highligthReplace(exp.description, hiReg)

      return exp
    })

    expertCopy.cv.experiences = expertCopy.experiences.sort((a, b) => moment(b.startDate).diff(a.startDate))

    return expertCopy
  }

  getAttachments(id) {
    return expertApi
      .getAttachments(id)
      .then(attachments => {
        this.attachments.replace(attachments)
      })
      .catch(err => {
        this.error = err
      })
  }

  deleteAttachment = id => {
    this.loading = true
    return expertApi
      .deleteAttachment(id)
      .then(() => {
        this.loading = false
        this.error = null

        return this.getAttachments(this.expert.id)
      })
      .catch(err => {
        this.loading = false
        console.error('Error: ', err)
        this.error = err
      })
  }

  getTimezone() {
    return this.staticData.getDataFromArray(
      this.expert.address?.timezoneId,
      'timezones'
    )
  }

  getIndustries() {
    const industryIds = this.expert.expert.subIndustries.map(s => s.industryId)
    const industryNames = industryIds.map(
      i => this.staticData.names.industries[i]
    )
    const formattedNames = industryNames.join(' / ')
    return formattedNames || ' - '
  }

  getSubIndustries() {
    const subIndustryNames = this.expert.expert.subIndustries.map(s => s.name)
    const formattedNames = subIndustryNames.join(' / ')
    return formattedNames || ' - '
  }

  getHourlyRate() {
    if (!this.expert.expert) {
      return
    }

    const { hourlyRate, hourlyRateCurrencyId } = this.expert.expert
    if (!hourlyRate || !hourlyRateCurrencyId) return

    const currencyName = this.staticData.names.currencies[hourlyRateCurrencyId]
    return `${hourlyRate} ${currencyName}`
  }

  setEmpty = action(() => {
    this.expert = new ExpertModel()
  })
}

export default ExpertStore
